// src/utils/http-client.js
import axios from "axios"
import { getAuthToken, removeAuthToken, refreshAccessTokenNow as refreshUserAccessTokenNow } from "./token-util"
import {
  removeAuthToken as removeCreatorToken,
  getAuthToken as getCreatorToken,
  refreshAccessTokenNow as refreshCreatorAccessTokenNow,
} from "./creatorTokenUtil"

// Dynamic URLs based on environment variables (fallback to production)
// Exported so callers that need a direct/plain URL (e.g. a file-download
// <a href>, not an Axios request) can build one without duplicating this
// fallback logic.
export const BASE_API = process.env.REACT_APP_BASE_URL || "https://blitz-app-u3rzf.ondigitalocean.app/api"
const ENGAGEMENT_API = process.env.REACT_APP_ENGAGEMENT_API_URL || "https://extract-data-delta.vercel.app/"



// create your axios instance
const Axios = axios.create({
  baseURL: BASE_API,
  timeout: 120_000,
  withCredentials: true,  // Enable sending cookies with requests (required for device_token cookie)
  headers: {
    "Content-Type": "application/json",
  },
})

// add your interceptors
Axios.interceptors.request.use(
  (config) => {
    const token = getAuthToken() || getCreatorToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add testing key for localhost development
    if (window.location.hostname === "localhost") {
      const testingKey = process.env.REACT_APP_TESTING_KEY
      if (testingKey) {
        config.headers["X-Testing-Key"] = testingKey
      }
    }

    // avoid bracketed arrays in query strings
    config.paramsSerializer = {
      indexes: null,
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * Classify authentication errors to determine if logout is necessary
 * @returns {boolean} true if token is truly invalid and logout required
 */
function isTokenInvalidationError(error) {
  const { config, response } = error;

  // Not an auth error at all
  if (!response || !config.headers?.Authorization) {
    return false;
  }

  // Respect ignoreAuthError flag
  if (config.ignoreAuthError) {
    return false;
  }

  // Explicit token expiry signals
  if (response.status === 422 && response.data?.error === "Invalid token") {
    return true;
  }

  // Check for specific error messages indicating token issues
  const errorMessage = response.data?.error || response.data?.message || '';
  const tokenErrors = [
    'token expired',
    'token invalid',
    'authentication failed',
    'invalid credentials',
    'jwt expired',
    'jwt malformed'
  ];

  if (typeof errorMessage === 'string' &&
      tokenErrors.some(msg => errorMessage.toLowerCase().includes(msg))) {
    return true;
  }

  // 401 Unauthorized - likely token issue, but check context
  if (response.status === 401) {
    // If it's a creator endpoint and we have creator token, might be permission issue
    const isCreatorEndpoint = config.url?.includes('/creatorconnect') ||
                              config.url?.includes('/creator');
    const hasCreatorToken = Boolean(getCreatorToken());

    if (isCreatorEndpoint && hasCreatorToken) {
      // Might be permission issue, not token invalidity
      console.warn('[http-client] 401 on creator endpoint - permission issue?', config.url);
      return false; // Don't auto-logout
    }

    return true; // Standard 401 - likely token expired
  }

  // 403 Forbidden - usually permission, NOT token invalidity
  if (response.status === 403) {
    console.warn('[http-client] 403 Forbidden - permission issue, not token expiry', config.url);
    return false; // Don't auto-logout for permission errors
  }

  return false;
}

Axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isRefreshRequest = Boolean(config?.url?.includes('/auth/refresh'));

    // Try one transparent refresh+retry for user token flows.
    if (
      response &&
      response.status === 401 &&
      config &&
      !config._retry &&
      !isRefreshRequest &&
      !config.ignoreAuthError &&
      (Boolean(getAuthToken()) || Boolean(getCreatorToken()))
    ) {
      config._retry = true;

      let refreshed = false;
      if (getAuthToken()) {
        refreshed = await refreshUserAccessTokenNow();
      } else if (getCreatorToken()) {
        refreshed = await refreshCreatorAccessTokenNow();
      }

      if (refreshed) {
        const latestToken = getAuthToken() || getCreatorToken();
        if (latestToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${latestToken}`;
          return Axios(config);
        }
      }
    }

    // Check if this is truly a token invalidation error
    if (isTokenInvalidationError(error)) {
      console.error('[http-client] Token invalidation detected:', {
        status: response.status,
        url: config.url,
        error: response.data
      });

      // Show user-friendly notification before logout
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.error('Your session has expired. Please log in again.', {
          duration: 5000
        });
      }

      // Small delay for notification to show
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Clear tokens and reload
      removeAuthToken();
      removeCreatorToken();
      window.location.reload();

      return Promise.reject(error);
    }

    // Handle permission errors gracefully (403)
    if (response && response.status === 403 && config.headers?.Authorization) {
      console.warn('[http-client] Permission denied:', config.url);

      // Add permission error flag to response
      error.isPermissionError = true;
      error.userFriendlyMessage = 'You do not have permission to access this resource.';

      return Promise.reject(error);
    }

    // Handle other 401 errors (might be temporary/permission)
    if (response && response.status === 401 && config.headers?.Authorization && !config.ignoreAuthError) {
      console.warn('[http-client] Authentication error (non-token):', config.url);

      // Add retry context
      error.isAuthError = true;
      error.userFriendlyMessage = 'Authentication error. Please try again.';

      return Promise.reject(error);
    }

    // Handle 404 with accept404 config
    if (config.accept404 && response && response.status === 404) {
      return { data: null };
    }

    return Promise.reject(error);
  }
)

export class HttpClient {
  static async get(url, params = null, config = {}) {
    const { data } = await Axios.get(url, { params, ...config })
    return data
  }

  static async post(url, body, config = {}) {
    const { data } = await Axios.post(url, body, config)
    return data
  }

  static async put(url, body, config = {}) {
    const { data } = await Axios.put(url, body, config)
    return data
  }

  static async patch(url, body, config = {}) {
    const { data } = await Axios.patch(url, body, config)
    return data
  }

  static async delete(url, config = {}) {
    const { data } = await Axios.delete(url, config)
    return data
  }
}

export const ConfigValue = {
  // if you still need a PORT constant, otherwise you can remove this
  PORT: 3000,

  // now hard-coded:
  PUBLIC_REST_API_ENDPOINT: BASE_API,
  PUBLIC_ENGAGEMENT_API_ENDPOINT: ENGAGEMENT_API,

  AUTH_TOKEN_KEY: "blitz-auth-token",
  AUTH_USER_INFO: "blitz-auth-user",
  AUTH_CREATOR_TOKEN_KEY: "blitz-creator-token",
  AUTH_CREATOR_USER_INFO: "blitz-creator-auth-user",
}
